import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CarouselDots from "@thkStore/Components/CarouselDots";
import {
    carouselContainer,
    carouselGridItem,
    carouselMainContainer,
    carouselNextArrowButton,
    carouselNextArrowGridItem,
    carouselPrevArrowButton,
    carouselPrevArrowGridItem,
    carouselSlide,
    carouselSlidesContainer,
    carouselWrapper,
} from "@thkStore/Styles/CarouselStyles";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { css, useTheme } from "styled-components";

interface CategoryItemModel {
    fields: {
        title: string;
        image: string;
        imageAltText: string;
        link: LinkFieldValue;
    };
}

const enum fields {
    items = "items",
    showImages = "showImages",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.items]: CategoryItemModel[];
        [fields.showImages]: boolean;
    };
}

export interface CategoryForSubcategoriesSelectStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    mobileColumn?: InjectableCss;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
}

export const categoryForSubcategoriesSelectStyles: CategoryForSubcategoriesSelectStyles = {
    mainContainer: carouselMainContainer,
    prevArrowGridItem: carouselPrevArrowGridItem,
    prevArrowButton: carouselPrevArrowButton,
    nextArrowGridItem: carouselNextArrowGridItem,
    nextArrowButton: carouselNextArrowButton,
    carouselGridItem,
    carouselWrapper,
    carouselContainer,
    carouselSlidesContainer,
    carouselSlide,
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
        `,
    },
    mobileColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            height: 100%;
            display: flex;
            transition: 0.5s;
            position: relative;

            &:hover {

                &:before {
                    width: 100%;
                }
            }

            &:before {
                content: "";
                position: absolute;
                z-index: 1;
                top: 0;
                left: 0;
                width: 80%;
                height: 2px;
                background-color: ${({ theme }: { theme: BaseTheme }) => theme.colors.primary.main};
                transition: 0.5s;
            }
        `,
    },
    subCategoryImageItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
            aspect-ratio: 8 / 9;

            [class^="LazyImageStyle-sc-"] {
                width: 100%;
            }

            img {
                object-fit: cover;
            }
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            position: absolute;
            background: linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0) 100%);
            width: 100%;
            height: 100%;
            padding: 31px 16px;
            align-items: flex-end;
        `,
    },
    subCategoryNameLink: {
        color: "primary.contrast",
        typographyProps: {
            weight: "bold",
            size: 16,
            css: css`
                width: 100%;
            `,
        },
    },
};

const styles = categoryForSubcategoriesSelectStyles;

const CategoryForSubcategoriesSelect: React.FC<OwnProps> = ({ fields: widgetFields }) => {
    const theme = useTheme() as BaseTheme;
    const { isInShell } = useShellContext();
    const items = widgetFields.items || [];
    const { links: itemLinks } = useGetLinks(items, o => o.fields.link);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const setCanScroll = () => {
        if (isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        const maxColumns = 4;
        if (typeof window === "undefined") {
            return maxColumns;
        }

        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            return 1;
        }
        if (innerWidth < theme.breakpoints.values[2]) {
            return 2;
        }
        if (innerWidth < theme.breakpoints.values[3]) {
            return 3;
        }
        return maxColumns;
    }, [theme]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        setSlidesToScroll(getSlidesToScroll());
    }, [windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || items.length === 0 || slidesToScroll === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: items.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, items.length]);

    if (items.length === 0) {
        return null;
    }

    const slideWidth = slidesToScroll > 0 ? `calc(100% / ${slidesToScroll})` : "25%";
    const canScroll = items.length > slidesToScroll;
    const isMobile = slidesToScroll > 0 && slidesToScroll < 3;

    const renderItem = (item: CategoryItemModel, index: number) => {
        const resolvedLink = itemLinks[index];
        const href = resolvedLink?.url || "";
        const imageUrl = item.fields.image ? parseImageUrl(item.fields.image).url : "";

        return (
            <GridContainer {...styles.innerContainer}>
                {widgetFields.showImages && (
                    <GridItem {...styles.subCategoryImageItem}>
                        {imageUrl && (
                            <LazyImage
                                src={imageUrl}
                                altText={item.fields.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.subCategoryNameLinkItem}>
                    <Link
                        href={href}
                        {...styles.subCategoryNameLink}
                        data-test-selector={`categoryForSubcategoriesSelectLink_${index}`}
                    >
                        {item.fields.title}
                    </Link>
                </GridItem>
            </GridContainer>
        );
    };

    if (isMobile) {
        return (
            <StyledWrapper
                {...styles.mobileColumn}
                data-test-selector="categoryForSubcategoriesSelect_mobile"
            >
                {items.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>{renderItem(item, index)}</React.Fragment>
                ))}
            </StyledWrapper>
        );
    }

    return (
        <>
        <GridContainer
            {...styles.mainContainer}
            data-test-selector="categoryForSubcategoriesSelect"
        >
            <GridItem {...styles.prevArrowGridItem}>
                {canScroll && (
                    <Button
                        {...styles.prevArrowButton}
                        onClick={() => embla && embla.scrollPrev()}
                        disabled={!canScrollPrev}
                        aria-label={translate("Previous")}
                        data-test-selector="prevBtn"
                    >
                        <ButtonIcon src={ChevronLeft} />
                    </Button>
                )}
            </GridItem>
            <GridItem {...styles.carouselGridItem}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                            {items.map((item, index) => (
                                <StyledWrapper
                                    {...styles.carouselSlide}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    style={{ width: slideWidth }}
                                >
                                    <StyledWrapper
                                        {...styles.carouselSlideInner}
                                        data-test-selector="carousel_slide"
                                    >
                                        {renderItem(item, index)}
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.nextArrowGridItem}>
                {canScroll && (
                    <Button
                        {...styles.nextArrowButton}
                        onClick={() => embla && embla.scrollNext()}
                        disabled={!canScrollNext}
                        aria-label={translate("Next")}
                        data-test-selector="nextBtn"
                    >
                        <ButtonIcon src={ChevronRight} />
                    </Button>
                )}
            </GridItem>
        </GridContainer>
        <CarouselDots embla={embla} />
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: CategoryForSubcategoriesSelect,
    definition: {
        group: "Categories",
        displayName: "Category For Subcategories Select",
        icon: "list-ul",
        allowedContexts: ["HomePage"],
        fieldDefinitions: [
            {
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title || "Item",
                defaultValue: [],
                fieldType: "Contextual",
                tab: contentTab,
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageAltText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
